import React from 'react'
import {
  useGetProjectAdminNotesQuery,
  useSaveProjectAdminNoteMutation,
  type AdminNoteEntry,
} from '@/redux/service/project'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertCircle,
  Lock,
  Clock,
  History,
  Save,
  RotateCcw,
  CheckCircle2,
  User,
} from 'lucide-react'
import { motion } from 'framer-motion'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { useCheckPermission } from '@/hooks/useCheckPermission'

const NOTE_MAX = 20000

interface AdminNotesProps {
  projectId: number
}

const formatAuthorTimestamp = (iso: string) => {
  const d = dayjs(iso)
  if (!d.isValid()) return ''
  return d.format('MMM D, YYYY [at] h:mma')
}

const initials = (name: string) =>
  name
    ?.split(' ')
    .filter(Boolean)
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'

const AdminNotes: React.FC<AdminNotesProps> = ({ projectId }) => {
  const {
    user,
    hasPageAccess,
    role,
    isLoading: isAuthLoading,
  } = useCheckPermission()

  const isAuthorized =
    role?.toLowerCase() === 'administrator' || hasPageAccess('admin_notes')

  const { data, isLoading, isError } = useGetProjectAdminNotesQuery(projectId, {
    skip: !projectId || !isAuthorized,
  })
  const [saveNote, { isLoading: isSaving }] = useSaveProjectAdminNoteMutation()

  const current = data?.data?.current ?? null
  const history = data?.data?.history ?? []

  const [draft, setDraft] = React.useState('')
  const [viewingId, setViewingId] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (current) {
      setDraft(current.note ?? '')
      setViewingId(current.id)
    } else {
      setDraft('')
      setViewingId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, current?.note])

  const isDirty = (current?.note ?? '') !== draft
  const overLimit = draft.length > NOTE_MAX
  const viewing =
    viewingId && current?.id !== viewingId
      ? history.find(h => h.id === viewingId)
      : null

  const handleSave = async () => {
    if (overLimit) {
      toast.error(
        `Note must be ${NOTE_MAX.toLocaleString()} characters or less`
      )
      return
    }
    try {
      const res = await saveNote({
        project_id: projectId,
        note: draft,
      }).unwrap()
      if (res.unchanged) {
        toast('No changes to save', { icon: 'ℹ️' })
      } else {
        toast.success('Admin note saved')
      }
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        'Failed to save admin note'
      toast.error(message)
    }
  }

  const handleReset = () => {
    setDraft(current?.note ?? '')
    setViewingId(current?.id ?? null)
  }

  const handleLoadVersion = (entry: AdminNoteEntry) => {
    setDraft(entry.note ?? '')
    setViewingId(entry.id)
  }

  /* ── Loading ── */
  if (isLoading || isAuthLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-48 rounded-lg' />
        <div className='border border-border rounded-xl bg-card shadow-sm p-6 space-y-4'>
          <Skeleton className='h-40 w-full rounded-lg' />
          <Skeleton className='h-9 w-28 rounded-lg' />
        </div>
      </div>
    )
  }

  /* ── Unauthorized ── */
  if (!isAuthorized) {
    return (
      <div className='border border-amber-500/20 bg-amber-500/5 rounded-xl p-12 text-center'>
        <div className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10'>
          <Lock className='h-6 w-6 text-amber-600 dark:text-amber-400' />
        </div>
        <h3 className='text-lg font-semibold text-foreground'>
          Access Restricted
        </h3>
        <p className='text-sm text-muted-foreground mt-2 max-w-sm mx-auto'>
          Admin notes are only visible to users with the Administrator role or
          specific access. You are currently logged in as{' '}
          <span className='font-medium text-foreground'>
            {user?.name || 'Guest'}
          </span>
          .
        </p>
      </div>
    )
  }

  /* ── Error ── */
  if (isError) {
    return (
      <div className='border border-destructive/20 bg-destructive/5 rounded-xl p-8 text-center'>
        <AlertCircle className='mx-auto mb-3 h-8 w-8 text-destructive/70' />
        <p className='text-sm font-semibold text-destructive'>
          Failed to load admin notes
        </p>
        <p className='text-xs text-muted-foreground mt-1'>
          Please try again later
        </p>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className='space-y-4'
    >
      {/* ── Header ── */}
      <div className='flex items-center justify-between flex-wrap gap-3'>
        <div className='flex items-center gap-3'>
          {/* Icon pill — same pattern as ProjectDetailsList header */}
          <div className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20'>
            <Lock className='w-4 h-4 text-amber-600 dark:text-amber-400' />
            <span className='text-sm font-semibold text-amber-700 dark:text-amber-400'>
              Admin Notes
            </span>
            <span className='text-[10px] font-bold uppercase tracking-wider text-amber-600/70 dark:text-amber-500/70 border-l border-amber-400/30 pl-2'>
              Private
            </span>
          </div>
          {current && (
            <div className='hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground'>
              <CheckCircle2 className='h-3.5 w-3.5 text-emerald-500 shrink-0' />
              <span>
                Saved by{' '}
                <span className='font-medium text-foreground capitalize'>
                  {current.created_by_name}
                </span>{' '}
                · {formatAuthorTimestamp(current.created_at)}
              </span>
            </div>
          )}
        </div>

        {/* User context badge */}
        <div className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/5 border border-primary/10'>
          <User className='w-3.5 h-3.5 text-primary/70' />
          <span className='text-[11px] font-medium text-muted-foreground'>
            Viewing as{' '}
            <span className='text-foreground font-semibold'>{user?.name}</span>
          </span>
          <span className='h-3 w-[1px] bg-border mx-1' />
          <span className='text-[10px] font-bold uppercase tracking-wider text-primary/80'>
            {role}
          </span>
        </div>
      </div>

      {/* ── Main card ── */}
      <div className='border border-border rounded-xl bg-card shadow-sm overflow-hidden'>
        <div className='grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border'>
          {/* ── Editor pane ── */}
          <div className='md:col-span-2 p-5 space-y-3'>
            {/* Version banner */}
            {viewing && (
              <div className='flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-500/8 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-400'>
                <History className='h-3.5 w-3.5 shrink-0 mt-0.5' />
                <span>
                  Viewing version by{' '}
                  <span className='font-semibold'>
                    {viewing.created_by_name}
                  </span>{' '}
                  · {formatAuthorTimestamp(viewing.created_at)}. Saving will
                  create a new version.
                </span>
              </div>
            )}

            {/* Textarea */}
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder='Write notes that should only be visible to admins…'
              className='w-full min-h-[260px] resize-y rounded-lg border border-border bg-background px-4 py-3 text-sm leading-relaxed font-mono outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all placeholder:text-muted-foreground/50'
              maxLength={NOTE_MAX + 500}
            />

            {/* Footer row */}
            <div className='flex items-center justify-between flex-wrap gap-3'>
              {/* Character count */}
              <span
                className={`text-xs font-medium tabular-nums ${
                  overLimit
                    ? 'text-destructive'
                    : draft.length > NOTE_MAX * 0.9
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-muted-foreground'
                }`}
              >
                {draft.length.toLocaleString()}&thinsp;/&thinsp;
                {NOTE_MAX.toLocaleString()}
                {overLimit && ' · limit exceeded'}
              </span>

              {/* Action buttons */}
              <div className='flex items-center gap-2'>
                {isDirty && (
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={handleReset}
                    disabled={isSaving}
                    className='h-8 text-xs border-border hover:bg-accent/30 transition-colors'
                  >
                    <RotateCcw className='h-3.5 w-3.5 mr-1.5' />
                    Reset
                  </Button>
                )}
                <Button
                  type='button'
                  size='sm'
                  onClick={handleSave}
                  disabled={isSaving || overLimit || !isDirty}
                  className='h-8 text-xs'
                >
                  <Save className='h-3.5 w-3.5 mr-1.5' />
                  {isSaving ? 'Saving…' : 'Save note'}
                </Button>
              </div>
            </div>
          </div>

          {/* ── Version history sidebar ── */}
          <div className='md:col-span-1 flex flex-col'>
            {/* Sidebar header */}
            <div className='flex items-center gap-2 px-4 py-3 bg-muted/40 border-b border-border'>
              <History className='h-3.5 w-3.5 text-muted-foreground shrink-0' />
              <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Version History
              </span>
              <span className='ml-auto text-xs text-muted-foreground tabular-nums'>
                {history.length} {history.length === 1 ? 'version' : 'versions'}
              </span>
            </div>

            {/* Sidebar body */}
            <div className='flex-1 max-h-[400px] overflow-y-auto'>
              {history.length === 0 ? (
                <div className='flex flex-col items-center justify-center py-12 px-4 text-center gap-2'>
                  <History className='h-7 w-7 text-muted-foreground/30' />
                  <p className='text-xs text-muted-foreground'>
                    No versions yet
                  </p>
                  <p className='text-[11px] text-muted-foreground/60'>
                    The first save will appear here
                  </p>
                </div>
              ) : (
                <ul className='divide-y divide-border/50'>
                  {history.map((entry, idx) => {
                    const isCurrent = idx === 0
                    const isActive = entry.id === viewingId
                    return (
                      <li key={entry.id}>
                        <button
                          type='button'
                          onClick={() => handleLoadVersion(entry)}
                          className={`w-full text-left px-4 py-3.5 transition-colors hover:bg-accent/30 ${
                            isActive ? 'bg-primary/5' : ''
                          }`}
                        >
                          <div className='flex items-start gap-3'>
                            {/* Avatar */}
                            <div className='h-7 w-7 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold border border-primary/20'>
                              {initials(entry.created_by_name)}
                            </div>

                            <div className='min-w-0 flex-1'>
                              <div className='flex items-center gap-1.5 flex-wrap'>
                                <span className='text-xs font-semibold truncate capitalize'>
                                  {entry.created_by_name}
                                </span>
                                {isCurrent && (
                                  <span className='inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'>
                                    Current
                                  </span>
                                )}
                              </div>
                              <div
                                className='text-[10px] text-muted-foreground/70 truncate mt-0.5'
                                title={entry.created_by_email}
                              >
                                {entry.created_by_email}
                              </div>
                              <div className='mt-1 flex items-center gap-1 text-[10px] text-muted-foreground'>
                                <Clock className='h-2.5 w-2.5 shrink-0' />
                                {formatAuthorTimestamp(entry.created_at)}
                              </div>
                              {entry.note ? (
                                <p className='mt-1.5 text-[11px] text-muted-foreground line-clamp-2 leading-snug'>
                                  {entry.note}
                                </p>
                              ) : (
                                <p className='mt-1.5 text-[11px] italic text-muted-foreground/50'>
                                  (cleared)
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default AdminNotes
